/**
 * Phone number helpers - backed by `libphonenumber-js`.
 *
 * Canonical stored form is E.164 (`+14155550123`) with an optional RFC
 * 3966 `;ext=<digits>` suffix (`+14155550123;ext=99`). The display
 * surface is the shared `<PhoneInput>`, which renders the country
 * flag/selector from `react-phone-number-input` alongside an extension
 * input. Legacy free-text values (from before the library landed) are
 * accepted on input and coerced to E.164 when they parse.
 */

import parsePhoneNumberFromString, {
  isPossiblePhoneNumber as libIsPossiblePhoneNumber,
  type CountryCode,
  type PhoneNumber,
} from 'libphonenumber-js'

export interface ParsedPhone {
  /** E.164-formatted base number (e.g. `+14155550123`), or `''`. */
  base: string
  /** Extension digits, or `''`. */
  extension: string
}

/** Matches a trailing RFC 3966 `;ext=<digits>` suffix. */
const EXT_SUFFIX_RE = /;ext=(\d+)\s*$/i

/**
 * Split a stored value into its base + extension. The base is returned
 * verbatim (still in whatever format the caller stored it in); pair
 * with `parsePhoneNumberFromString` to normalise further.
 */
export function splitStoredValue(raw: string | null | undefined): ParsedPhone {
  if (!raw) return { base: '', extension: '' }
  const match = raw.match(EXT_SUFFIX_RE)
  if (match) {
    return { base: raw.slice(0, match.index).trim(), extension: match[1] }
  }
  return { base: raw.trim(), extension: '' }
}

/**
 * Combine a base (E.164) and extension back into the canonical stored
 * form. Empty base + empty extension collapses to `''`.
 */
export function combinePhoneValue(base: string, extension: string): string {
  const cleanBase = (base ?? '').trim()
  const cleanExt = (extension ?? '').replace(/\D/g, '')
  if (!cleanBase && !cleanExt) return ''
  if (!cleanBase) return cleanExt ? `;ext=${cleanExt}` : ''
  return cleanExt ? `${cleanBase};ext=${cleanExt}` : cleanBase
}

export const PHONE_EXAMPLE = '(415) 555-0123'

export const PHONE_VALIDATION_MESSAGE =
  `Enter a phone number that matches the selected country (e.g. ${PHONE_EXAMPLE})`

/**
 * True when the base portion of a stored value parses as a possible phone
 * number for the selected country according to `libphonenumber-js`.
 * Extensions are ignored - the base number itself is what has to be
 * plausible. This is intentionally less strict than carrier-level
 * assignment validity so realistic-looking demo/test numbers still pass.
 */
export function isValidPhone(
  raw: string | null | undefined,
  defaultCountry?: CountryCode,
): boolean {
  if (!raw) return false
  const { base } = splitStoredValue(raw)
  if (!base) return false
  try {
    return libIsPossiblePhoneNumber(base, defaultCountry)
  } catch {
    return false
  }
}

/**
 * Best-effort conversion of a free-text value to E.164. Returns the
 * original value if it can't be parsed so callers never silently drop
 * user input.
 */
export function toE164(raw: string, defaultCountry?: CountryCode): string {
  if (!raw) return ''
  try {
    const parsed = parsePhoneNumberFromString(raw, defaultCountry)
    return parsed?.number ?? raw
  } catch {
    return raw
  }
}

/**
 * Format a stored phone value for display. Parses E.164 into
 * international format (e.g. `+1 415 555 0123`) and appends
 * the extension when present (e.g. `+1 415 555 0123 ext. 99`).
 * Falls back to the raw value when it can't be parsed.
 */
export function formatPhoneDisplay(raw: string | null | undefined): string {
  if (!raw) return ''
  const { base, extension } = splitStoredValue(raw)
  if (!base) return extension ? `ext. ${extension}` : ''

  let formatted: string
  try {
    const parsed: PhoneNumber | undefined = parsePhoneNumberFromString(base)
    formatted = parsed ? parsed.formatInternational() : base
  } catch {
    formatted = base
  }

  return extension ? `${formatted} ext. ${extension}` : formatted
}
